<template>
  <div class="purchase-return-create">
    <div class="page-header">
      <h2>创建退货单</h2>
    </div>

    <el-form :model="form" :rules="rules" ref="form" label-width="120px">
      <el-card class="box-card">
        <div slot="header" class="card-header">
          <span>选择进货单</span>
        </div>

        <el-form-item label="进货单号" prop="purchaseId">
          <el-select
            v-model="form.purchaseId"
            filterable
            placeholder="请选择进货单"
            @change="handlePurchaseChange"
            style="width: 100%"
          >
            <el-option
              v-for="item in purchaseList"
              :key="item.id"
              :label="`${item.purchaseNo} - ${item.supplierName} - ¥${item.totalAmount.toFixed(2)}`"
              :value="item.id"
            />
          </el-select>
        </el-form-item>

        <div v-if="selectedPurchase" class="purchase-info">
          <el-descriptions :column="3" border>
            <el-descriptions-item label="供应商">{{ selectedPurchase.supplierName }}</el-descriptions-item>
            <el-descriptions-item label="进货日期">{{ selectedPurchase.purchaseDate }}</el-descriptions-item>
            <el-descriptions-item label="总金额">¥{{ selectedPurchase.totalAmount.toFixed(2) }}</el-descriptions-item>
          </el-descriptions>
        </div>
      </el-card>

      <el-card class="box-card" v-if="selectedPurchase">
        <div slot="header" class="card-header">
          <span>退货商品</span>
        </div>

        <el-table :data="form.items" border style="width: 100%">
          <el-table-column type="selection" width="55" />
          <el-table-column prop="productName" label="商品名称" />
          <el-table-column prop="quantity" label="进货数量" width="120" />
          <el-table-column prop="returnedQuantity" label="已退数量" width="120" />
          <el-table-column label="退货数量" width="150">
            <template slot-scope="scope">
              <el-input-number
                v-model="scope.row.returnQuantity"
                :min="0"
                :max="scope.row.quantity - scope.row.returnedQuantity"
                :disabled="scope.row.returnedQuantity >= scope.row.quantity"
                size="small"
              />
            </template>
          </el-table-column>
          <el-table-column prop="unitPrice" label="单价" width="120">
            <template slot-scope="scope">
              ¥{{ scope.row.unitPrice.toFixed(2) }}
            </template>
          </el-table-column>
          <el-table-column label="小计" width="120">
            <template slot-scope="scope">
              ¥{{ (scope.row.returnQuantity * scope.row.unitPrice).toFixed(2) }}
            </template>
          </el-table-column>
        </el-table>

        <div class="total-amount">
          退货总金额：<span class="amount">¥{{ totalReturnAmount.toFixed(2) }}</span>
        </div>
      </el-card>

      <el-card class="box-card" v-if="selectedPurchase">
        <div slot="header" class="card-header">
          <span>退货原因</span>
        </div>

        <el-form-item label="退货原因" prop="reasonCode">
          <el-select v-model="form.reasonCode" placeholder="请选择退货原因" style="width: 100%">
            <el-option label="质量问题" value="QUALITY_ISSUE" />
            <el-option label="发错货" value="WRONG_ITEM" />
            <el-option label="数量不对" value="WRONG_QUANTITY" />
            <el-option label="运输损坏" value="DAMAGED" />
            <el-option label="过期/临期" value="EXPIRED" />
            <el-option label="规格不符" value="SPEC_MISMATCH" />
            <el-option label="其他" value="OTHER" />
          </el-select>
        </el-form-item>

        <el-form-item label="退货说明" prop="reasonText">
          <el-input
            v-model="form.reasonText"
            type="textarea"
            :rows="3"
            placeholder="请详细描述退货原因"
          />
        </el-form-item>

        <el-form-item label="上传凭证">
          <el-upload
            action="/api/files/upload"
            list-type="picture-card"
            :on-success="handleUploadSuccess"
            :on-remove="handleUploadRemove"
            :file-list="fileList"
          >
            <i class="el-icon-plus"></i>
          </el-upload>
        </el-form-item>
      </el-card>

      <div class="form-footer">
        <el-button @click="goBack">取消</el-button>
        <el-button type="primary" @click="submitForm" :loading="submitting">提交退货申请</el-button>
      </div>
    </el-form>
  </div>
</template>

<script>
import { createPurchaseReturn } from '@/api/purchaseReturn'
import { getPurchaseOrderList } from '@/api/purchase'

export default {
  name: 'PurchaseReturnCreate',
  data() {
    return {
      form: {
        purchaseId: '',
        reasonCode: '',
        reasonText: '',
        evidenceUrls: [],
        items: []
      },
      rules: {
        purchaseId: [
          { required: true, message: '请选择进货单', trigger: 'change' }
        ],
        reasonCode: [
          { required: true, message: '请选择退货原因', trigger: 'change' }
        ],
        reasonText: [
          { required: true, message: '请输入退货说明', trigger: 'blur' }
        ]
      },
      purchaseList: [],
      selectedPurchase: null,
      fileList: [],
      submitting: false
    }
  },
  computed: {
    totalReturnAmount() {
      return this.form.items.reduce((sum, item) => {
        return sum + (item.returnQuantity * item.unitPrice)
      }, 0)
    }
  },
  mounted() {
    this.loadPurchaseList()
  },
  methods: {
    async loadPurchaseList() {
      try {
        const res = await getPurchaseOrderList({ status: 'RECEIVED', size: 100 })
        this.purchaseList = res.data.content
      } catch (error) {
        this.$message.error('加载进货单列表失败')
      }
    },
    handlePurchaseChange(purchaseId) {
      this.selectedPurchase = this.purchaseList.find(p => p.id === purchaseId)
      if (this.selectedPurchase) {
        this.loadPurchaseItems(purchaseId)
      }
    },
    async loadPurchaseItems(purchaseId) {
      try {
        const res = await getPurchaseOrderList({ id: purchaseId })
        const purchase = res.data.content.find(p => p.id === purchaseId)
        if (purchase && purchase.items) {
          this.form.items = purchase.items.map(item => ({
            ...item,
            returnQuantity: 0,
            returnedQuantity: item.returnedQuantity || 0
          }))
        }
      } catch (error) {
        this.$message.error('加载进货单明细失败')
      }
    },
    handleUploadSuccess(res, file, fileList) {
      if (res.code === 200) {
        this.form.evidenceUrls = fileList.map(f => f.response.data.url)
      }
    },
    handleUploadRemove(file, fileList) {
      this.form.evidenceUrls = fileList.map(f => f.response.data.url)
    },
    submitForm() {
      this.$refs.form.validate(async (valid) => {
        if (!valid) return

        const validItems = this.form.items.filter(item => item.returnQuantity > 0)
        if (validItems.length === 0) {
          this.$message.warning('请至少选择一个退货商品')
          return
        }

        this.submitting = true
        try {
          const data = {
            purchaseId: this.form.purchaseId,
            reasonCode: this.form.reasonCode,
            reasonText: this.form.reasonText,
            evidenceUrls: JSON.stringify(this.form.evidenceUrls),
            items: validItems.map(item => ({
              purchaseItemId: item.id,
              productId: item.productId,
              returnQuantity: item.returnQuantity
            }))
          }

          await createPurchaseReturn(data)
          this.$message.success('退货申请提交成功')
          this.$router.push('/wholesaler/purchase-return-list')
        } catch (error) {
          this.$message.error(error.response?.data?.message || '提交失败')
        } finally {
          this.submitting = false
        }
      })
    },
    goBack() {
      this.$router.go(-1)
    }
  }
}
</script>

<style scoped>
.purchase-return-create {
  padding: 20px;
}

.page-header {
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  font-size: 20px;
}

.box-card {
  margin-bottom: 20px;
}

.card-header {
  font-weight: bold;
}

.purchase-info {
  margin-top: 15px;
}

.total-amount {
  margin-top: 15px;
  text-align: right;
  font-size: 16px;
}

.total-amount .amount {
  color: #f56c6c;
  font-weight: bold;
  font-size: 20px;
}

.form-footer {
  text-align: center;
  margin-top: 30px;
}
</style>
